// taste-profile.js — the learning loop for coffee.
//
// Three flows:
//   1. checkInToBiasDelta(checkIn) — turn a single morning check-in into deltas
//   2. applyDeltaToProfile(profile, delta) — fold deltas into the running taste profile
//   3. applyTasteToBrew(brew, profile) — bias a freshly-computed brew toward the user's
//      learned preferences (run after calculateAidenBrew, before showing to the user)
//
// Biases are bounded — even after a hundred sour-rated brews you can't end up
// at grind setting 1 forever. Bounds let the math correct itself when tastes
// shift (new beans, seasonal water changes, new grinder).

import { applyProfileOverrides } from "./coffee";

// Bounds on the running biases. Stay within ranges that make physical sense.
const BIAS_BOUNDS = {
  grind_bias:        { min: -2, max: 2 },   // ±2 Ode clicks total drift
  temp_bias_c:       { min: -3, max: 3 },   // ±3°C
  ratio_bias:        { min: -2, max: 2 },   // ±2 ratio points
  bloom_time_bias_s: { min: -20, max: 20 }, // ±20s
};

// Each check-in nudges biases by these step sizes (small — multi-day learning).
const STEP = {
  grind: 0.25,        // 1/4 of an Ode click per check-in
  temp_c: 0.5,        // half a degree
  ratio: 0.2,
  bloom_time_s: 3,
};

// Decay factor: every check-in pulls existing biases very slightly toward zero.
// Prevents one strong-tasting bag locking the model on tight ratios for years.
const DECAY = 0.97;

/**
 * Turn a check-in into a bias delta object. Pure function — no I/O.
 *
 * Check-in shape:
 *   { rating: 1-5, taste: ["sour"|"bitter"|...], body: "thin"|"balanced"|"heavy",
 *     would_repeat: bool, notes: string }
 */
export function checkInToBiasDelta(checkIn) {
  const delta = { grind_bias: 0, temp_bias_c: 0, ratio_bias: 0, bloom_time_bias_s: 0 };
  const taste = checkIn.taste || [];
  const notes = (checkIn.notes || "").toLowerCase();
  const text = taste.join(" ") + " " + notes;
  const has = (k) => text.includes(k);

  // Underextracted (sour/acidic/weak): finer grind, hotter temp, longer bloom
  if (has("sour") || has("under") || has("acidic")) {
    delta.grind_bias  += STEP.grind;
    delta.temp_bias_c += STEP.temp_c;
    delta.bloom_time_bias_s += STEP.bloom_time_s;
  }
  // Overextracted (bitter/astringent): coarser, cooler
  if (has("bitter") || has("over") || has("astringent") || has("burnt")) {
    delta.grind_bias  -= STEP.grind;
    delta.temp_bias_c -= STEP.temp_c;
  }
  // Weak/thin: tighter ratio
  if (has("weak") || has("thin") || has("watery") || checkIn.body === "thin") {
    delta.ratio_bias += STEP.ratio;
  }
  // Heavy/sludgy: looser ratio, coarser
  if (has("muddy") || has("sludgy") || has("heavy") || checkIn.body === "heavy") {
    delta.ratio_bias -= STEP.ratio * 0.6;
    delta.grind_bias -= STEP.grind * 0.5;
  }
  // Low rating without specific notes: small generic adjustment toward "more extraction"
  // (more often the issue is underextraction in home brewing)
  if ((checkIn.rating || 5) <= 2 && taste.length === 0 && !notes.trim()) {
    delta.grind_bias += STEP.grind * 0.5;
    delta.temp_bias_c += STEP.temp_c * 0.5;
  }
  // High rating amplifies LITTLE — we don't want positive feedback to lock biases.
  // Just record the count. The decay alone will keep the model honest.

  return delta;
}

/**
 * Fold a delta into the existing taste profile. Apply decay first, then add deltas,
 * then clamp to bounds.
 */
export function applyDeltaToProfile(profile, delta) {
  const next = { ...profile };

  // Decay existing biases slightly toward zero
  for (const key of Object.keys(BIAS_BOUNDS)) {
    next[key] = (profile[key] || 0) * DECAY + (delta[key] || 0);
    next[key] = clamp(next[key], BIAS_BOUNDS[key].min, BIAS_BOUNDS[key].max);
  }

  next.sample_count = (profile.sample_count || 0) + 1;
  next.last_check_in_at = new Date().toISOString();

  // Infer broad preferences from accumulated biases (only after we have data)
  if (next.sample_count >= 4) {
    next.preferred_strength = next.ratio_bias > 0.4 ? "strong"
      : next.ratio_bias < -0.4 ? "light"
      : "balanced";
    next.preferred_clarity = next.grind_bias < -0.4 ? "clean"
      : next.grind_bias > 0.4 ? "heavy"
      : "balanced";
  }

  return next;
}

/**
 * Apply the user's taste profile as biases on top of a freshly computed brew.
 * This is the "learning -> action" step — every morning's brew benefits from
 * everything you've taught it.
 */
export function applyTasteToBrew(brew, tasteProfile) {
  if (!brew || !tasteProfile || !tasteProfile.sample_count) return brew;

  // Round biases into actionable adjustments (whole grind clicks, integer temps).
  const grindShift  = Math.round(tasteProfile.grind_bias || 0);
  const tempShift   = Math.round(tasteProfile.temp_bias_c || 0);
  const ratioShift  = +(tasteProfile.ratio_bias || 0).toFixed(1);
  const bloomShift  = Math.round(tasteProfile.bloom_time_bias_s || 0);

  // Skip if nothing meaningful
  if (!grindShift && !tempShift && !ratioShift && !bloomShift) return brew;

  // Use applyProfileOverrides for the aiden profile fields.
  const p = brew.aidenProfile;
  const overrides = {};
  if (ratioShift) overrides.ratio = clamp(p.ratio - ratioShift, 10, 22); // smaller ratio = stronger
  if (bloomShift && p.bloomEnabled) overrides.bloomDuration = clamp(p.bloomDuration + bloomShift, 0, 120);
  if (tempShift) {
    if (p.bloomEnabled) overrides.bloomTemperature = clamp(p.bloomTemperature + tempShift, 50, 99);
    overrides.ssPulseTemperatures = (p.ssPulseTemperatures || []).map(t => clamp(t + tempShift, 50, 99));
    overrides.batchPulseTemperatures = (p.batchPulseTemperatures || []).map(t => clamp(t + tempShift, 50, 99));
  }

  const tuned = applyProfileOverrides(brew, overrides);

  // Adjust grind separately — not in aidenProfile, lives on the Ode.
  if (grindShift) {
    tuned.grind = {
      ...tuned.grind,
      setting: clamp(tuned.grind.setting - grindShift, 1, 11), // negative shift = finer
    };
  }

  // Mark that we biased this brew, with a human-readable explanation.
  tuned.taste_applied = {
    sample_count: tasteProfile.sample_count,
    adjustments: explainAdjustments(grindShift, tempShift, ratioShift, bloomShift),
  };
  return tuned;
}

function explainAdjustments(grind, temp, ratio, bloom) {
  const parts = [];
  if (grind > 0) parts.push(`${grind} click${grind > 1 ? "s" : ""} finer (you trend underextracted)`);
  if (grind < 0) parts.push(`${-grind} click${grind < -1 ? "s" : ""} coarser (you trend overextracted)`);
  if (temp > 0) parts.push(`+${temp}°C (you like more extraction)`);
  if (temp < 0) parts.push(`${temp}°C (you prefer cooler)`);
  if (ratio > 0) parts.push(`tighter ratio (you like it stronger)`);
  if (ratio < 0) parts.push(`looser ratio (you like it lighter)`);
  if (bloom > 0) parts.push(`+${bloom}s bloom (you favor longer degas)`);
  if (bloom < 0) parts.push(`${bloom}s bloom (you favor shorter degas)`);
  return parts;
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

/**
 * Decide if we should ask the user for a check-in this morning.
 * Returns the brew log to ask about, or null.
 *
 * Rules:
 *   - There must be a brew logged in the last 24 hours
 *   - That brew must not already have a check-in
 *   - The current local time must be within the morning window (5am - 12pm)
 *   - We haven't already asked today (last_check_in_at is not today)
 */
export function findCheckInTarget(brewLogs, checkIns, lastDismissedAt, opts = {}) {
  const now = new Date();
  // By default this is a *morning* prompt (5am–noon). Pass { ignoreHours: true }
  // to surface it any time there's an unrated recent brew (used by the hero,
  // which makes "rate yesterday" the start of the flow whenever you open it).
  if (!opts.ignoreHours) {
    const hour = now.getHours();
    if (hour < 5 || hour >= 12) return null;
  }

  // If user dismissed today, don't ask again
  if (lastDismissedAt && isSameLocalDay(new Date(lastDismissedAt), now)) return null;

  const cutoff = now.getTime() - 36 * 3600 * 1000; // last 36 hours, gives wiggle for late-night brews
  const recent = (brewLogs || []).find(l =>
    new Date(l.brewed_at).getTime() >= cutoff
  );
  if (!recent) return null;

  const alreadyChecked = (checkIns || []).some(c => c.brew_log_id === recent.id);
  if (alreadyChecked) return null;

  return recent;
}

function isSameLocalDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

// Default empty taste profile
export const EMPTY_TASTE_PROFILE = {
  grind_bias: 0,
  temp_bias_c: 0,
  ratio_bias: 0,
  bloom_time_bias_s: 0,
  preferred_strength: "balanced",
  preferred_clarity: "balanced",
  sample_count: 0,
  last_check_in_at: null,
  insights: {},
};
